import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Offcanvas from 'react-bootstrap/Offcanvas';
import DarkLogo from "src/assets/images/dark_logo.png";


import {
    HiViewGrid, HiMenu, HiOutlineSearch,
    HiOutlineShoppingCart, HiOutlineUserCircle,
    HiOutlineLogout,
    HiOutlineHome, HiOutlineShoppingBag, HiHeart,
    HiOutlineQuestionMarkCircle, HiOutlineLogin

} from "react-icons/hi";

import { authSelector, logOut } from 'src/store/reducers/auth';
import classes from './styles.module.css';
import { Link, NavLink } from 'react-router-dom';
import logo from 'src/assets/images/logo.svg';
import CatsMenu from '../cats/CatsMenu';
import { cartCountSelector, cartReset } from 'src/store/reducers/cart';
import { clearAllStorage } from 'src/utils/auth';
import { isRTL } from 'src/utils/global';
import apiSlice from 'src/store/api/apiSlice';
import { useWishlistQuery } from 'src/store/api/accounts/accountsApiSlice';
import { useGuestWishlistState } from 'src/hooks';


const ICON_SIZE = 22
const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `${classes.item__link} nav-link ${isActive ? classes.active : ""}`;


export default () => {
    const { t } = useTranslation(['common']);
    const dispatch = useDispatch();

    const [openMenu, setOpenMenu] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const auth = useSelector(authSelector);
    const { slugs: guestWishlistSlugs, syncState: guestWishlistSyncState } = useGuestWishlistState();
    const isGuestWishlistMode = !auth.access || (guestWishlistSyncState === "syncing" && guestWishlistSlugs.length > 0);
    const { data: wishlistData } = useWishlistQuery(undefined, {
        refetchOnReconnect: true,
        refetchOnMountOrArgChange: true,
        skip: isGuestWishlistMode,
    });
    const wishlistCount = isGuestWishlistMode ? guestWishlistSlugs.length : (wishlistData?.count ?? 0);
    const wishlistCountLabel = wishlistCount > 99 ? "99+" : wishlistCount;
    const cartCount = useSelector(cartCountSelector);
    const cartCountLabel = cartCount > 99 ? "99+" : cartCount;

    // =============================
    // Handlers ====================
    // =============================
    const openMenuHandle = () => setOpenMenu(true);

    const logoutHandle = () => {
        dispatch(logOut());
        dispatch(cartReset());
        dispatch(apiSlice.util.resetApiState());
        clearAllStorage();
    };



    return (
        <header>
            <Navbar key={'xl'} fixed="top" expand={false}
                expanded={expanded}
                onToggle={setExpanded}
                className={`${classes.nav} mb-3 ${classes.nav__sm} ${isRTL() && classes.rtl}`}>
                <Container fluid>
                    <button
                        type="button"
                        className={classes.cat__toggle}
                        onClick={openMenuHandle}
                        aria-label={t("nav.categories.title")}
                    >
                        <HiViewGrid size={28} />
                    </button>

                    <Navbar.Brand href="/">
                        <img src={logo} alt='Malek Store' height={43} width={96} />
                    </Navbar.Brand>

                    <Navbar.Toggle className={classes.toggle} aria-controls={`offcanvasNavbar-expand-xl`} >
                        <HiMenu size={ICON_SIZE + 4} />
                    </Navbar.Toggle>

                    <Navbar.Offcanvas
                        id={`offcanvasNavbar-expand-xl`}
                        aria-labelledby={`offcanvasNavbarLabel-expand-xl`}
                        placement="end"
                        className={`${classes.sm__offcanvas} ${isRTL() && classes.rtl__offcanvas}`}
                        style={{ direction: isRTL() ? 'rtl' : 'ltr' }}
                    >
                        <Offcanvas.Header closeButton className={classes.offcanvas__header}>
                            <Offcanvas.Title id={`offcanvasNavbarLabel-expand-xl`}>
                                <img src={DarkLogo} alt={t("store_name")} height={50} width={123} />
                            </Offcanvas.Title>
                        </Offcanvas.Header>

                        <Offcanvas.Body className={classes.offcanvas__body}>
                            {/* Any item in the menu is a navigation action, so close the
                                offcanvas as soon as one is clicked. */}
                            <Nav className="justify-content-end flex-grow-1 pe-3" onClick={() => setExpanded(false)}>
                                <NavLink className={navLinkClass} to="/">
                                    <HiOutlineHome />
                                    {t("nav.home")}
                                </NavLink>
                                <NavLink className={navLinkClass} to="/products">
                                    <HiOutlineShoppingBag />
                                    {t("nav.products")}
                                </NavLink>
                                <NavLink className={navLinkClass} to="wishlist/">
                                    <span className={classes.navIconWrap}>
                                        <HiHeart />
                                        {wishlistCount > 0 ? (
                                            <span className={classes.navCountBadge}>{wishlistCountLabel}</span>
                                        ) : null}
                                    </span>
                                    {t("nav.wishlist")}
                                </NavLink>
                                <Link className={`${classes.item__link} nav-link`} to={'/products?search=true'}>
                                    <HiOutlineSearch />
                                    {t("nav.search")}
                                </Link>
                                <NavLink className={navLinkClass} to={'/cart'}>
                                    <span className={classes.navIconWrap}>
                                        <HiOutlineShoppingCart />
                                        {cartCount > 0 ? (
                                            <span className={classes.navCountBadge}>{cartCountLabel}</span>
                                        ) : null}
                                    </span>
                                    {t("nav.cart")}
                                </NavLink>
                                <NavLink className={navLinkClass} to="about-us/">
                                    <HiOutlineQuestionMarkCircle />
                                    {t("nav.about_us")}
                                </NavLink>
                                {auth.access ?
                                    <>
                                        <NavLink className={navLinkClass} to={'/profile'}>
                                            <HiOutlineUserCircle />
                                            {t("nav.profile")}
                                        </NavLink>
                                        <Nav.Link className={`${classes.item__link} nav-link`} onClick={logoutHandle}>
                                            <HiOutlineLogout />
                                            {t('nav.logout')}
                                        </Nav.Link>
                                    </>
                                    :
                                    <NavLink to={"/auth/login/"} className={`${classes.nav__btn} mt-4`} >
                                        <HiOutlineLogin />
                                        {t("nav.signIn")}
                                    </NavLink>
                                }
                            </Nav>
                        </Offcanvas.Body>
                    </Navbar.Offcanvas>
                </Container>
                <CatsMenu openMenu={openMenu} setOpenMenu={setOpenMenu} />
            </Navbar>
            <div className={classes.prop}>
            </div>
        </header >
    );
}
